import type { FC} from 'react';
import { useState, useEffect, useRef } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, alpha, Box, Button, CheckCircleIcon, CloseIcon, CookieIcon, Dialog, DialogActions, DialogContent, DialogTitle, ExpandMoreIcon, FormControlLabel, IconButton, Stack, Switch, Typography, useTheme } from '@/shared/ui/mui';
import { useResponsive } from '@/shared/hooks/useResponsive';
import type {
  CookieCategories} from '@/shared/ui/cookie-consent';
import {
  cookieConsentService,
  COOKIE_DESCRIPTIONS,
} from '@/shared/ui/cookie-consent';

interface CookiePreferencesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (categories: CookieCategories) => void;
}

export const CookiePreferencesModal: FC<CookiePreferencesModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [categories, setCategories] = useState<CookieCategories>({
    essential: true,
    analytics: false,
    functional: false,
    marketing: false,
  });

  // Load existing consent on mount
  useEffect(() => {
    const existingConsent = cookieConsentService.getConsent();
    if (existingConsent) {
      setCategories(existingConsent.categories);
    }
  }, []);

  // Focus trap: focus first button when dialog opens
  useEffect(() => {
    if (open && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector('button');
      firstButton?.focus();
    }
  }, [open]);

  const handleToggle = (category: keyof CookieCategories) => {
    if (category === 'essential') return; // Cannot toggle essential cookies

    setCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    onSave(categories);
    onClose();
  };

  const handleAcceptAll = () => {
    const allAccepted: CookieCategories = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    setCategories(allAccepted);
    onSave(allAccepted);
    onClose();
  };

  const handleRejectAll = () => {
    const onlyEssential: CookieCategories = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    setCategories(onlyEssential);
    onSave(onlyEssential);
    onClose();
  };

  const renderCookieCategory = (
    category: keyof CookieCategories,
    index: number,
  ) => {
    const description = COOKIE_DESCRIPTIONS[category];
    const isEssential = category === 'essential';

    return (
      <Accordion
        key={category}
        defaultExpanded={index === 0}
        sx={{
          '&:before': { display: 'none' },
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 1,
          '&:last-child': { mb: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`cookie-${category}-content`}
          id={`cookie-${category}-header`}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              pr: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="medium">
                {description.title}
              </Typography>
              {isEssential && (
                <Typography variant="caption" color="text.secondary">
                  Always Active
                </Typography>
              )}
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={categories[category]}
                  onChange={() => handleToggle(category)}
                  disabled={isEssential}
                  color="primary"
                  onClick={e => e.stopPropagation()}
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {description.description}
            </Typography>

            <Box>
              <Typography
                variant="caption"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Purpose:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {description.purpose}
              </Typography>
            </Box>

            <Box>
              <Typography
                variant="caption"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Examples:
              </Typography>
              <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                {description.examples.map(example => (
                  <Typography
                    key={example}
                    component="li"
                    variant="caption"
                    color="text.secondary"
                  >
                    {example}
                  </Typography>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="caption"
                fontWeight="medium"
                display="block"
                gutterBottom
              >
                Duration:
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {description.duration}
              </Typography>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      aria-labelledby="cookie-preferences-title"
      aria-describedby="cookie-preferences-description"
      aria-modal="true"
      role="dialog"
    >
      <DialogTitle id="cookie-preferences-title">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CookieIcon sx={{ color: 'white', fontSize: 24 }} />
            </Box>
            <Typography variant="h6">Cookie Preferences</Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close cookie preferences"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" id="cookie-preferences-description">
            We use cookies to enhance your experience and analyze our traffic.
            You can customize your preferences below. Your consent will expire
            after 12 months.
          </Typography>
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Choose which types of cookies you want to allow. You can change
            these settings at any time.
          </Typography>
        </Box>

        <Stack spacing={1}>
          {(
            Object.keys(COOKIE_DESCRIPTIONS) as (keyof CookieCategories)[]
          ).map((category, index) => renderCookieCategory(category, index))}
        </Stack>

        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: alpha(theme.palette.success.light, 0.1),
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
          <Typography variant="body2" color="text.secondary">
            Your preferences will be saved and you can change them anytime via
            the {'"'}Cookie Settings{'"'} link in the footer.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={handleRejectAll}
          variant="outlined"
          color="inherit"
          sx={{ minWidth: 120 }}
        >
          Reject All
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSave}
          variant="outlined"
          color="primary"
          sx={{ minWidth: 120 }}
        >
          Save Preferences
        </Button>
        <Button
          onClick={handleAcceptAll}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 120,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          }}
        >
          Accept All
        </Button>
      </DialogActions>
    </Dialog>
  );
};
